// LAP-768 — Moteur de notation du Conseiller Social / Relationnel.
//
// Strict miroir de `buildProAdvisor` (LAP-767) : on transforme l'agrégat
// renvoyé par /api/Social/dashboard/ en une note /100 sur 5 dimensions, un
// diagnostic (forces / faiblesses / risques / non évalué), une décomposition
// (répartition des interactions par cercle), des actions prioritaires et des
// recommandations narratives.
//
// Forme des données consommées (cf. Social/social_dashboard_views.py) :
//   {
//     overview:     { total_contacts, interactions_this_month, ratio_pro,
//                     ratio_perso, yoy_change_pct },
//     repartition:  [{ label: 'family'|'pro'|'friendship'|'intimacy', value }],
//     evolution:    { months: [{ label, total, by_bucket }], highlights: [...] },
//     key_relations:{ top_active: [...], to_reactivate: [...] },
//     suggested_tasks:      [{ contact_id, contact_name, rating, days_since, … }],
//     suggested_tasks_meta: { threshold_days },
//     insights:     [{ icon, text }],
//   }

const identityT = (key, opts) =>
  opts && typeof opts.defaultValue === 'string' ? opts.defaultValue : key

// Poids des 5 dimensions (somme = 1). On met l'accent sur l'activité réelle et
// l'entretien des relations importantes — c'est le cœur de valeur du module.
export const SOCIAL_DIMENSION_WEIGHTS = {
  activite: 0.25,
  entretien: 0.25,
  diversite: 0.2,
  equilibre: 0.15,
  dynamique: 0.15,
}

// Cible d'activité : ~12 interactions/mois (≈ 3 par semaine) = score 100.
export const SOCIAL_ACTIVITY_TARGET = 12
// Au-delà de ce backlog de relances en attente, la dimension « entretien »
// tombe à 0 (8+ contacts importants laissés sans nouvelles).
export const SOCIAL_BACKLOG_ZERO_AT = 8

// 4 cercles canoniques exposés par l'API (ordre stable pour l'UI).
export const SOCIAL_BUCKET_ORDER = ['family', 'pro', 'friendship', 'intimacy']
const SOCIAL_BUCKETS = {
  family: { color: '#f472b6', labelKey: 'conseillerSocial.buckets.family', labelDefault: 'Famille' },
  pro: { color: '#63a0ff', labelKey: 'conseillerSocial.buckets.pro', labelDefault: 'Pro' },
  friendship: { color: '#34d399', labelKey: 'conseillerSocial.buckets.friendship', labelDefault: 'Amis' },
  intimacy: { color: '#a78bfa', labelKey: 'conseillerSocial.buckets.intimacy', labelDefault: 'Couple' },
}

const CATEGORY_COLORS = {
  activite: '#63a0ff',
  entretien: '#f472b6',
  diversite: '#34d399',
  equilibre: '#a78bfa',
  dynamique: '#fbbf24',
}

function num(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function safeArr(value) {
  return Array.isArray(value) ? value : []
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value))
}

// LAP-768 — Même barème de statut que les autres conseillers.
function statusFromScore(score) {
  if (score >= 70) return 'good'
  if (score >= 45) return 'warning'
  return 'critical'
}

function bucketLabel(key, t) {
  const conf = SOCIAL_BUCKETS[key]
  if (!conf) return key
  return t(conf.labelKey, { defaultValue: conf.labelDefault })
}

/**
 * Activité relationnelle : volume d'interactions du mois vs cible.
 * Disponible dès que l'utilisateur a au moins un contact.
 */
function computeActivite(overview, totalContacts) {
  const interactions = num(overview?.interactions_this_month)
  if (totalContacts <= 0) {
    return { score: null, available: false, interactions: 0 }
  }
  const score = clamp(Math.round((interactions / SOCIAL_ACTIVITY_TARGET) * 100))
  return { score, available: true, interactions }
}

/**
 * Entretien des relations clés : plus de contacts importants (≥ 4★) sont
 * en attente de relance, plus le score baisse. Le backlog provient de
 * `suggested_tasks` (déjà filtré et trié côté backend).
 */
function computeEntretien(suggestedTasks, totalContacts, thresholdDays) {
  if (totalContacts <= 0) {
    return { score: null, available: false, backlog: 0, thresholdDays }
  }
  const tasks = safeArr(suggestedTasks)
  const backlog = tasks.length
  const score = clamp(Math.round(100 - (backlog / SOCIAL_BACKLOG_ZERO_AT) * 100))
  // Contact le plus « en retard » pour la reco ciblée.
  let oldest = null
  tasks.forEach((task) => {
    const days = task?.days_since
    if (days == null) {
      // jamais contacté → priorité maximale
      if (!oldest || oldest.days != null) oldest = { name: task?.contact_name, days: null }
    } else if (!oldest || (oldest.days != null && num(days) > oldest.days)) {
      oldest = { name: task?.contact_name, days: num(days) }
    }
  })
  return { score, available: true, backlog, thresholdDays, oldest }
}

/**
 * Diversité du réseau : entropie normalisée de la répartition des
 * interactions sur les 4 cercles. Un réseau concentré sur un seul cercle
 * obtient un score plancher (30), un réseau parfaitement réparti obtient 100.
 */
function computeDiversite(repartition) {
  const buckets = safeArr(repartition).map((b) => ({
    label: b?.label,
    value: num(b?.value),
  }))
  const total = buckets.reduce((a, b) => a + b.value, 0)
  if (total <= 0) {
    return { score: null, available: false, total: 0, activeBuckets: 0 }
  }
  let entropy = 0
  buckets.forEach((b) => {
    if (b.value > 0) {
      const p = b.value / total
      entropy += -p * Math.log(p)
    }
  })
  const maxEntropy = Math.log(SOCIAL_BUCKET_ORDER.length) // ln(4)
  const normalized = maxEntropy > 0 ? entropy / maxEntropy : 0
  const score = clamp(Math.round(30 + normalized * 70))
  const activeBuckets = buckets.filter((b) => b.value > 0).length
  return { score, available: true, total, activeBuckets }
}

/**
 * Équilibre pro / perso. Cohérent avec la logique de la carte Insights du
 * Social Dashboard : un réseau trop « pro » est pénalisé, un bon équilibre
 * (voire une dominante perso) est valorisé.
 * Disponible quand des interactions ont pu être catégorisées (répartition > 0).
 */
function computeEquilibre(overview, repartitionTotal) {
  if (repartitionTotal <= 0) {
    return { score: null, available: false, ratioPro: null, ratioPerso: null }
  }
  const ratioPerso = clamp(num(overview?.ratio_perso), 0, 100)
  const ratioPro = clamp(num(overview?.ratio_pro), 0, 100)
  const score = clamp(Math.round(40 + ratioPerso * 0.6))
  return { score, available: true, ratioPro, ratioPerso }
}

/**
 * Dynamique relationnelle : tendance du volume d'interactions. On compare
 * le dernier mois au précédent (mois sur mois) ; à défaut, on retombe sur la
 * comparaison N vs N-1 (`yoy_change_pct`).
 */
function computeDynamique(evolution, overview) {
  const months = safeArr(evolution?.months)
  const yoy = overview?.yoy_change_pct

  let score = null
  let available = false
  let trendPct = null
  let basis = null

  if (months.length >= 2) {
    const prev = num(months[months.length - 2]?.total)
    const curr = num(months[months.length - 1]?.total)
    if (prev > 0) {
      trendPct = Math.round(((curr - prev) / prev) * 100)
      score = clamp(Math.round(50 + trendPct))
      available = true
      basis = 'mom'
    } else if (curr > 0) {
      trendPct = 100
      score = 75
      available = true
      basis = 'mom'
    }
  }

  if (!available && yoy != null && Number.isFinite(Number(yoy))) {
    trendPct = Math.round(num(yoy))
    score = clamp(Math.round(50 + trendPct))
    available = true
    basis = 'yoy'
  }

  return { score, available, trendPct, basis }
}

/**
 * buildSocialAdvisor — moteur de score social / relationnel.
 *
 * @param {object} params
 * @param {object} params.dashboard - agrégat /api/Social/dashboard/.
 * @param {object} params.user - profil utilisateur (pour le prénom).
 * @param {Function} params.t - fonction i18next.
 * @returns {object} agrégat consommé par ConseillerSocialPage.
 */
export function buildSocialAdvisor({ dashboard = {}, user = {}, t = identityT } = {}) {
  const firstName = user?.first_name || user?.firstName || user?.prenom || ''

  const overview = dashboard?.overview || {}
  const repartition = safeArr(dashboard?.repartition)
  const evolution = dashboard?.evolution || {}
  const suggestedTasks = safeArr(dashboard?.suggested_tasks)
  const thresholdDays = num(dashboard?.suggested_tasks_meta?.threshold_days) || 30
  const insights = safeArr(dashboard?.insights)
  const totalContacts = num(overview?.total_contacts)
  const repartitionTotal = repartition.reduce((a, b) => a + num(b?.value), 0)

  const activite = computeActivite(overview, totalContacts)
  const entretien = computeEntretien(suggestedTasks, totalContacts, thresholdDays)
  const diversite = computeDiversite(repartition)
  const equilibre = computeEquilibre(overview, repartitionTotal)
  const dynamique = computeDynamique(evolution, overview)

  // ── Dimensions ─────────────────────────────────────────────────────────
  const dimensions = [
    {
      key: 'activite',
      label: t('conseillerSocial.dimensions.activite', { defaultValue: 'Activité relationnelle' }),
      score: activite.score,
      available: activite.available,
      weight: SOCIAL_DIMENSION_WEIGHTS.activite,
      route: '/social-dashboard',
      detail: t('conseillerSocial.dimensions.activiteDetail', {
        defaultValue:
          'Volume de vos interactions ce mois-ci (cible ~12, soit environ 3 par semaine).',
      }),
      dataSource: t('conseillerSocial.dimensions.activiteData', {
        defaultValue: 'Données : événements et messages échangés ce mois-ci.',
      }),
      emptyHint: t('conseillerSocial.dimensions.activiteEmpty', {
        defaultValue:
          'Aucun contact enregistré — ajoutez vos contacts pour activer ce score.',
      }),
    },
    {
      key: 'entretien',
      label: t('conseillerSocial.dimensions.entretien', { defaultValue: 'Entretien des relations clés' }),
      score: entretien.score,
      available: entretien.available,
      weight: SOCIAL_DIMENSION_WEIGHTS.entretien,
      route: '/social-dashboard',
      detail: t('conseillerSocial.dimensions.entretienDetail', {
        defaultValue:
          'Vos contacts importants (≥ 4★) sont-ils relancés à temps ? Plus le retard s’accumule, plus le score baisse.',
      }),
      dataSource: t('conseillerSocial.dimensions.entretienData', {
        defaultValue: 'Données : vos relances suggérées (contacts importants inactifs).',
      }),
      emptyHint: t('conseillerSocial.dimensions.entretienEmpty', {
        defaultValue:
          'Aucun contact enregistré — ajoutez et notez vos contacts pour activer ce score.',
      }),
    },
    {
      key: 'diversite',
      label: t('conseillerSocial.dimensions.diversite', { defaultValue: 'Diversité du réseau' }),
      score: diversite.score,
      available: diversite.available,
      weight: SOCIAL_DIMENSION_WEIGHTS.diversite,
      route: '/social-dashboard',
      detail: t('conseillerSocial.dimensions.diversiteDetail', {
        defaultValue:
          'Vos interactions sont-elles réparties entre vos différents cercles (famille, amis, pro, couple) ?',
      }),
      dataSource: t('conseillerSocial.dimensions.diversiteData', {
        defaultValue: 'Données : répartition de vos interactions sur 3 mois.',
      }),
      emptyHint: t('conseillerSocial.dimensions.diversiteEmpty', {
        defaultValue:
          'Pas encore d’interactions catégorisées — créez des événements avec participants pour activer ce score.',
      }),
    },
    {
      key: 'equilibre',
      label: t('conseillerSocial.dimensions.equilibre', { defaultValue: 'Équilibre pro / perso' }),
      score: equilibre.score,
      available: equilibre.available,
      weight: SOCIAL_DIMENSION_WEIGHTS.equilibre,
      route: '/social-dashboard',
      detail: t('conseillerSocial.dimensions.equilibreDetail', {
        defaultValue:
          'Part de votre vie relationnelle consacrée au perso vs au pro. Un réseau trop « pro » fragilise votre cercle personnel.',
      }),
      dataSource: t('conseillerSocial.dimensions.equilibreData', {
        defaultValue: 'Données : ratio pro / perso de vos interactions du mois.',
      }),
      emptyHint: t('conseillerSocial.dimensions.equilibreEmpty', {
        defaultValue:
          'Pas encore d’interactions catégorisées — ce score s’activera dès vos premiers événements.',
      }),
    },
    {
      key: 'dynamique',
      label: t('conseillerSocial.dimensions.dynamique', { defaultValue: 'Dynamique relationnelle' }),
      score: dynamique.score,
      available: dynamique.available,
      weight: SOCIAL_DIMENSION_WEIGHTS.dynamique,
      route: '/social-dashboard',
      detail: t('conseillerSocial.dimensions.dynamiqueDetail', {
        defaultValue:
          'Vos interactions sont-elles en hausse ou en baisse par rapport au mois précédent ?',
      }),
      dataSource: t('conseillerSocial.dimensions.dynamiqueData', {
        defaultValue: 'Données : évolution de vos interactions sur les derniers mois.',
      }),
      emptyHint: t('conseillerSocial.dimensions.dynamiqueEmpty', {
        defaultValue:
          'Pas assez d’historique — ce score s’activera après quelques semaines d’activité.',
      }),
    },
  ].map((d) => ({
    ...d,
    status: d.available && d.score != null ? statusFromScore(d.score) : 'unknown',
  }))

  // ── Score global ─────────────────────────────────────────────────────────
  // Moyenne pondérée sur les seules dimensions évaluées, poids re-normalisés.
  const evaluatedDims = dimensions.filter((d) => d.available && d.score != null)
  const evaluatedWeight = evaluatedDims.reduce((acc, d) => acc + d.weight, 0)
  const globalScore =
    evaluatedWeight > 0
      ? Math.round(
          evaluatedDims.reduce((acc, d) => acc + d.score * d.weight, 0) / evaluatedWeight,
        )
      : null

  // ── Diagnostic ───────────────────────────────────────────────────────────
  const strengths = dimensions
    .filter((d) => d.available && d.status === 'good')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const weaknesses = dimensions
    .filter((d) => d.available && d.status === 'warning')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const risks = dimensions
    .filter((d) => d.available && d.status === 'critical')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const notEvaluated = dimensions
    .filter((d) => !d.available)
    .map((d) => ({ key: d.key, title: d.label, detail: d.emptyHint }))

  // ── Décomposition : répartition des interactions par cercle ───────────────
  const breakdownRaw = SOCIAL_BUCKET_ORDER.map((key) => {
    const entry = repartition.find((b) => b?.label === key)
    return {
      key,
      category: bucketLabel(key, t),
      value: num(entry?.value),
      color: SOCIAL_BUCKETS[key].color,
    }
  })
  const breakdownTotal = breakdownRaw.reduce((acc, e) => acc + e.value, 0)
  const breakdownEntries = breakdownRaw
    .map((e) => ({
      ...e,
      percent: breakdownTotal > 0 ? Math.round((e.value / breakdownTotal) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value)

  // ── Actions prioritaires ──────────────────────────────────────────────────
  const actionsPrioritaires = []
  const actionable = dimensions
    .filter((d) => d.available && d.score != null && d.status !== 'good')
    .sort((a, b) => a.score - b.score)
  actionable.forEach((d) => {
    actionsPrioritaires.push({
      id: `improve-${d.key}`,
      title: t('conseillerSocial.actions.improveTitle', {
        defaultValue: `Renforcer : ${d.label.toLowerCase()}`,
        category: d.label,
      }),
      description: d.detail,
      impact: d.status === 'critical' ? 'high' : 'medium',
      route: d.key === 'entretien' ? '/MesContacts' : d.route,
    })
  })
  notEvaluated.forEach((d) => {
    const dim = dimensions.find((x) => x.key === d.key)
    actionsPrioritaires.push({
      id: `complete-${d.key}`,
      title: t('conseillerSocial.actions.completeTitle', {
        defaultValue: `Compléter : ${d.title.toLowerCase()}`,
        category: d.title,
      }),
      description: d.detail,
      impact: 'low',
      route: dim ? dim.route : '/social-dashboard',
    })
  })

  // ── Recommandation mise en avant (top insight backend) ────────────────────
  const recommendation = pickLeadInsight(insights)

  // ── Recommandations narratives (repli sans appel LLM) ─────────────────────
  const recommendations = []
  if (entretien.available && entretien.backlog > 0) {
    const who = entretien.oldest?.name
    recommendations.push(
      t('conseillerSocial.reco.entretien', {
        defaultValue:
          who
            ? `${entretien.backlog} contact${entretien.backlog > 1 ? 's' : ''} important${entretien.backlog > 1 ? 's' : ''} attend${entretien.backlog > 1 ? 'ent' : ''} une relance. Commencez par ${who} — un simple message suffit à raviver le lien.`
            : `${entretien.backlog} contact${entretien.backlog > 1 ? 's' : ''} important${entretien.backlog > 1 ? 's' : ''} attend${entretien.backlog > 1 ? 'ent' : ''} une relance. Un simple message suffit à raviver le lien.`,
        count: entretien.backlog,
        name: who || '',
      }),
    )
  }
  if (activite.available && activite.score != null && activite.score < 70) {
    recommendations.push(
      t('conseillerSocial.reco.activite', {
        defaultValue: `Vous avez eu ${activite.interactions} interaction${activite.interactions > 1 ? 's' : ''} ce mois-ci. Planifier un ou deux moments (appel, café, déjeuner) relancerait votre dynamique.`,
        count: activite.interactions,
      }),
    )
  }
  if (equilibre.available && equilibre.ratioPro != null && equilibre.ratioPro >= 70) {
    recommendations.push(
      t('conseillerSocial.reco.equilibre', {
        defaultValue: `${equilibre.ratioPro}% de vos interactions sont pro. Pensez à investir votre cercle perso : famille et amis se nourrissent de régularité.`,
        pct: equilibre.ratioPro,
      }),
    )
  }
  if (diversite.available && diversite.activeBuckets <= 1) {
    recommendations.push(
      t('conseillerSocial.reco.diversite', {
        defaultValue:
          'Vos interactions se concentrent sur un seul cercle. Élargir (renouer avec un ami, un proche) rend votre réseau plus résilient.',
      }),
    )
  }
  if (dynamique.available && dynamique.trendPct != null && dynamique.trendPct <= -20) {
    recommendations.push(
      t('conseillerSocial.reco.dynamique', {
        defaultValue: `Vos interactions ont baissé de ${Math.abs(dynamique.trendPct)}% par rapport au mois précédent. Un petit rituel hebdomadaire aide à inverser la tendance.`,
        pct: Math.abs(dynamique.trendPct),
      }),
    )
  }
  if (totalContacts <= 0) {
    recommendations.push(
      t('conseillerSocial.reco.noContacts', {
        defaultValue:
          'Commencez par ajouter vos contacts et noter les plus importants : c’est la base d’un suivi relationnel utile.',
      }),
    )
  }

  // ── Prompts pour le chat conseiller ────────────────────────────────────────
  const suggestedPrompts = [
    t('conseillerSocial.prompts.reconnect', {
      defaultValue: 'Qui devrais-je recontacter en priorité ?',
    }),
    t('conseillerSocial.prompts.balance', {
      defaultValue: 'Comment rééquilibrer ma vie pro et perso ?',
    }),
    t('conseillerSocial.prompts.routine', {
      defaultValue: 'Quel rituel mettre en place pour entretenir mes relations ?',
    }),
    t('conseillerSocial.prompts.network', {
      defaultValue: 'Comment diversifier et élargir mon réseau ?',
    }),
  ]

  return {
    userProfile: {
      firstName,
    },
    totals: {
      totalContacts,
      interactionsThisMonth: activite.interactions,
      ratioPro: equilibre.ratioPro,
      ratioPerso: equilibre.ratioPerso,
      yoyChangePct: overview?.yoy_change_pct ?? null,
      reactivationBacklog: entretien.backlog,
      thresholdDays,
      activeBuckets: diversite.activeBuckets,
      trendPct: dynamique.trendPct,
    },
    metrics: {
      interactionsThisMonth: activite.interactions,
      reactivationBacklog: entretien.backlog,
      activeBuckets: diversite.activeBuckets,
      trendPct: dynamique.trendPct,
    },
    socialScore: {
      global: globalScore,
      dimensions,
      evaluatedCount: evaluatedDims.length,
      totalCount: dimensions.length,
    },
    diagnostic: {
      strengths,
      weaknesses,
      risks,
      notEvaluated,
    },
    breakdown: {
      entries: breakdownEntries,
      total: breakdownTotal,
    },
    actionsPrioritaires,
    recommendation,
    recommendations,
    suggestedPrompts,
  }
}

/**
 * LAP-768 — Sélectionne l'insight backend à mettre en avant : on privilégie un
 * avertissement (icon 'warning'), sinon le premier insight disponible.
 */
export function pickLeadInsight(insights) {
  const list = safeArr(insights)
  if (list.length === 0) return ''
  const warning = list.find((i) => i?.icon === 'warning')
  const chosen = warning || list[0]
  return typeof chosen?.text === 'string' ? chosen.text.trim() : ''
}

export { CATEGORY_COLORS, SOCIAL_BUCKETS }
